import { Box, Flex } from '@osmosis/react-ui/components/Box';
import React, { useId, useState } from 'react';
import Modal from '../Modal';
import { useFetch } from '../../context/FetchProvider';
import { useQuizContext } from '../../context/QuizContext';
import { useQuery } from 'react-query';
import { getBundle, getItems } from '../../lib/frontend/quiz';
import { QuizTypeGroup } from '../../constants';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import {
  QuizAccuracy,
  QuizConfidence,
  QuizFilterState,
  QuizItem,
} from '../../types/quiz';
import { messages } from './constants';
import { ModalHookProps } from '../../hooks/useModal';
import { P } from '@osmosis/react-ui/components/Text';
import QuestionFilterPanel from './QuizQuestionFilterPanel';
import useQuizProgress from './useQuizProgress';
import { getEstimatedTime } from '../../lib/frontend/quiz/scoreboard';
import QuizSummaryLegend from './QuizSummaryLegend';
import QuizSummaryModalHeader from './QuizSummaryModalHeader';
import SemiCircleProgressBar from '../SemiCircleProgressBar';
import { Button } from '@osmosis/react-ui/components/Button';
import { useQueueContext } from '../../context/QueueProvider';
import { useUserContext } from '../../context/UserContext';

type Props = {
  modalProps: ModalHookProps<{ items: number[] }>;
  onStart: (items: QuizItem[]) => void;
  items?: number[];
  quizType?: QuizTypeGroup;
};

const getItemType = (
  quizType: QuizTypeGroup,
  count: number,
  intl: IntlShape,
  showCount?: boolean,
) => {
  let text;
  const countStr = showCount ? `${count} ` : '';
  switch (quizType) {
    case 'FLASHCARD':
      text = intl.formatMessage(
        {
          defaultMessage: '{count}Flashcards',
          id: 'quiz.start_modal.flashcards',
        },
        { count: countStr },
      );
      break;
    case 'QUESTION':
    default:
      text = intl.formatMessage(
        {
          defaultMessage: '{count}Questions',
          id: 'quiz.start_modal.questions',
        },
        { count: countStr },
      );
      break;
  }

  return text;
};

/**
 * Filters quiz items based on the active filters.
 */
export const filterQuizItems = (
  items: QuizItem[],
  activeFilters: QuizFilterState = {
    remaining: false,
    correctHigh: false,
    correctMedium: false,
    correctLow: false,
    incorrectHigh: false,
    incorrectMedium: false,
    incorrectLow: false,
  },
) => {
  // If no filters selected, return all
  if (
    !activeFilters.remaining &&
    !activeFilters.correctHigh &&
    !activeFilters.correctMedium &&
    !activeFilters.correctLow &&
    !activeFilters.incorrectHigh &&
    !activeFilters.incorrectMedium &&
    !activeFilters.incorrectLow
  ) {
    return items;
  }

  return items.filter((item) => {
    const answered = !!item.history;
    const accuracy = item.history?.lastAccuracy;
    const confidence = item.history?.lastConfidence;

    // Remaining (unanswered)
    if (activeFilters.remaining && !answered) return true;

    // Correct
    if (
      activeFilters.correctHigh &&
      accuracy === QuizAccuracy.CORRECT &&
      confidence === QuizConfidence.HIGH
    )
      return true;
    if (
      activeFilters.correctMedium &&
      accuracy === QuizAccuracy.CORRECT &&
      confidence === QuizConfidence.MEDIUM
    )
      return true;
    if (
      activeFilters.correctLow &&
      accuracy === QuizAccuracy.CORRECT &&
      confidence === QuizConfidence.LOW
    )
      return true;

    // Incorrect
    if (
      activeFilters.incorrectHigh &&
      accuracy === QuizAccuracy.INCORRECT &&
      confidence === QuizConfidence.HIGH
    )
      return true;
    if (
      activeFilters.incorrectMedium &&
      accuracy === QuizAccuracy.INCORRECT &&
      confidence === QuizConfidence.MEDIUM
    )
      return true;
    if (
      activeFilters.incorrectLow &&
      accuracy === QuizAccuracy.INCORRECT &&
      confidence === QuizConfidence.LOW
    )
      return true;

    return false;
  });
};

/**
 * QuizStartModalNew component.
 * @param props - The properties for the component.
 */
const QuizStartModalNew = (props: Props) => {
  const { modalProps, items: _items, quizType, onStart } = props;
  const items = modalProps.data?.items || _items || [];
  const { saveToQueue, queues } = useQueueContext();
  const { user, hasPrime } = useUserContext();
  const titleId = useId();
  const [filters, setFilters] = React.useState({
    remaining: false,
    correctHigh: false,
    correctMedium: false,
    correctLow: false,
    incorrectHigh: false,
    incorrectMedium: false,
    incorrectLow: false,
  });
  const isAnyFilterSelected = Object.values(filters).some(Boolean);
  const { quiz, bundleID } = useQuizContext();
  const intl = useIntl();
  const { get, post } = useFetch();
  const { data: quizItems = [], isLoading } = useQuery(
    items,
    async () => {
      if (quiz.length) return quiz.map((q) => q.item);
      if (bundleID) return await getBundle(get, bundleID);
      const quizItems = await getItems(post, items);
      return quizItems;
    },
    { initialData: quiz.map((q) => q.item) },
  );
  const [filteredQuizItems, setFilteredQuizItems] = useState(quizItems);
  const estimatedTime = getEstimatedTime(filteredQuizItems.length, quizType);

  const itemTypeTextNoCount = getItemType(
    quizType ?? 'QUESTION',
    0,
    intl,
    false,
  );

  // To check if the quiz is from a queue
  const currentQueueIdsQuestions = Object.values(queues.questions?.index ?? {})
    .map((i) => i.todo)
    .flat();
  const currentQueueIdsFlashcards = Object.values(
    queues.flashcards?.index ?? {},
  )
    .map((i) => i.todo)
    .flat();
  const currentQueueIds = [
    ...currentQueueIdsQuestions,
    ...currentQueueIdsFlashcards,
  ];
  const queueHasAll = quizItems
    .map((item) => item.nodeID)
    .every((nodeID: number) => currentQueueIds.includes(nodeID));

  React.useEffect(() => {
    setFilteredQuizItems(quizItems); // Reset when quizItems change
  }, [quizItems]);

  const handleFilterChange = (filterKey: keyof typeof filters) => {
    setFilters((prev) => {
      const updated = { ...prev, [filterKey]: !prev[filterKey] };
      setFilteredQuizItems(filterQuizItems(quizItems, updated));
      return updated;
    });
  };

  // Use filteredQuizItems for progress bar
  const {
    totalItems = filteredQuizItems.length,
    correctItems = [],
    incorrectItems = [],
  } = useQuizProgress(
    filteredQuizItems.map((item) => ({
      item,
      answered: !!item.history,
      accuracy: item.history?.lastAccuracy,
      confidence: item.history?.lastConfidence,
      time: item.history?.timestamp,
    })),
  );
  const hasAnyAnswered = filteredQuizItems.some((item) => !!item.history);
  const handleSave = () => {
    saveToQueue(
      quizItems.map((item) => item.nodeID),
      quizType === 'FLASHCARD' ? 'flashcards' : 'questions',
    );
  };

  return (
    <Modal
      _className="max-h-[90vh]"
      role="dialog"
      aria-labelledby={titleId}
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus
      {...modalProps}
    >
      <QuizSummaryModalHeader estimatedTime={estimatedTime} titleId={titleId} />
      <Box
        sx={{ padding: 3, display: 'flex', justifyContent: 'space-between' }}
      >
        <P data-testid="quiz-summary-totalCount">
          {quizType === 'FLASHCARD'
            ? intl.formatMessage(messages.flashcardsTotal, {
                totalCount: quizItems.length,
              })
            : intl.formatMessage(messages.questionsTotal, {
                totalCount: quizItems.length,
              })}
        </P>
        <Flex sx={{ gap: 3 }}>
          {!queueHasAll && (
            <Button
              isLoading={isLoading}
              onClick={handleSave}
              data-testid="modal-start-save-deck"
              variant="outline"
              enabled={!!user && hasPrime}
              sx={{ color: '#134986', borderColor: '#134986' }}
            >
              <FormattedMessage
                defaultMessage="Save Deck"
                id="quiz.save.button"
              />
            </Button>
          )}
          <Button
            isLoading={isLoading}
            onClick={() => onStart(filteredQuizItems)}
            data-testid="modal-start-button"
          >
            <FormattedMessage
              defaultMessage="Start Deck"
              id="quiz.start_modal.button.start"
            />
          </Button>
        </Flex>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 3,
          position: 'relative',
        }}
        width={600}
        maxWidth="100%"
        mx="auto"
      >
        <SemiCircleProgressBar
          correct={correctItems.length}
          incorrect={incorrectItems.length}
          total={totalItems}
        />
        <Box
          style={{
            position: 'absolute',
            left: 0,
            width: '100%',
            transform: 'translateY(-50%)',
            padding: '8px 0',
            textAlign: 'center',
            fontFamily: 'inherit',
          }}
          sx={hasAnyAnswered ? { top: '50%' } : { top: '60%' }}
        >
          <Box>
            <P
              sx={{ fontSize: 16, fontWeight: 500 }}
              data-testid="quiz.summary.quizTypeTextCount"
            >
              {quizType === 'FLASHCARD'
                ? intl.formatMessage(messages.flashcards, {
                    count: filteredQuizItems.length,
                  })
                : intl.formatMessage(messages.questions, {
                    count: filteredQuizItems.length,
                  })}
            </P>
          </Box>
        </Box>
        {/* show indivisual count */}
        {(isAnyFilterSelected || hasAnyAnswered) && (
          <QuizSummaryLegend
            correctCount={correctItems.length}
            incorrectCount={incorrectItems.length}
            remainingCount={
              totalItems - (correctItems.length + incorrectItems.length)
            }
            filters={filters}
          />
        )}
      </Box>
      <QuestionFilterPanel
        quizItems={quizItems}
        filters={filters}
        quizType={itemTypeTextNoCount}
        onFilterChange={handleFilterChange}
      />
    </Modal>
  );
};

export default QuizStartModalNew;
